import Select from 'react-select';
import { Alert } from 'reactstrap';
import { useTranslation } from 'react-i18next';

const RECURRENCE_OPTIONS = (t) => [
  { value: 'NONE', label: t('shared.recurrence_none', 'Does not repeat') },
  { value: 'DAILY', label: t('shared.daily', 'Daily') },
  { value: 'WEEKLY', label: t('shared.weekly', 'Weekly') },
  { value: 'MONTHLY', label: t('shared.monthly', 'Monthly') },
  { value: 'YEARLY', label: t('shared.yearly', 'Yearly') },
];

const DAYS_OF_WEEK = (t) => [
  { value: 0, label: t('shared.sun', 'Sun') },
  { value: 1, label: t('shared.mon', 'Mon') },
  { value: 2, label: t('shared.tue', 'Tue') },
  { value: 3, label: t('shared.wed', 'Wed') },
  { value: 4, label: t('shared.thu', 'Thu') },
  { value: 5, label: t('shared.fri', 'Fri') },
  { value: 6, label: t('shared.sat', 'Sat') },
];

const INTERVAL_OPTIONS = {
  WEEKLY: Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: `${i + 1}` })),
  MONTHLY: Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: `${i + 1}` })),
  YEARLY: Array.from({ length: 5 }, (_, i) => ({ value: i + 1, label: `${i + 1}` })),
};

const DAY_OF_MONTH_OPTIONS = (lang) => Array.from({ length: 31 }, (_, i) => ({
  value: i + 1,
  label: getOrdinal(i + 1, lang),
}));

function getOrdinal(n, lang) {
  if (lang === 'fr') return `${n}`;
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const RecurrenceSelector = ({
  recurrenceType = 'NONE',
  recurrenceDays = [],
  recurrenceInterval = 1,
  recurrenceDayOfMonth = 1,
  workType,
  scheduledDate,
  onChange,
  errors = {},
  showAlertInline = true
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const dayOfMonthOptions = DAY_OF_MONTH_OPTIONS(lang);
  
  const handleRecurrenceTypeChange = (selected) => {
    const newType = selected?.value || 'NONE';
    let newDays = [];

    if (newType === 'DAILY') {
      // Daily = all days selected
      newDays = [0, 1, 2, 3, 4, 5, 6];
    } else if (newType === 'WEEKLY') {
      // Weekly defaults to weekdays
      newDays = [1, 2, 3, 4, 5];
    }

    onChange({
      recurrenceType: newType,
      recurrenceDays: newDays,
      recurrenceInterval: 1,
      recurrenceDayOfMonth: newType === 'MONTHLY' ? 1 : null,
    });
  };

  const handleDayToggle = (dayValue) => {
    let newDays;
    if (recurrenceDays.includes(dayValue)) {
      newDays = recurrenceDays.filter(d => d !== dayValue);
    } else {
      newDays = [...recurrenceDays, dayValue].sort((a, b) => a - b);
    }
    onChange({ recurrenceDays: newDays });
  };

  const handleIntervalChange = (selected) => {
    onChange({ recurrenceInterval: selected?.value || 1 });
  };

  const handleDayOfMonthChange = (selected) => {
    onChange({ recurrenceDayOfMonth: selected?.value || 1 });
  };

  // Generate helper text
  const getHelperText = () => {
    if (recurrenceType === 'NONE') return null;

    const dayNames = [
      t('shared.sunday', 'Sunday'),
      t('shared.monday', 'Monday'),
      t('shared.tuesday', 'Tuesday'),
      t('shared.wednesday', 'Wednesday'),
      t('shared.thursday', 'Thursday'),
      t('shared.friday', 'Friday'),
      t('shared.saturday', 'Saturday')
    ];
    const interval = Number(recurrenceInterval) || 1;
    const dayOfMonth = Number(recurrenceDayOfMonth) || 1;

    switch (recurrenceType) {
      case 'DAILY': {
        return t('business.components.recurrence_selector.daily_helper', 'Repeats every day');
      }
      case 'WEEKLY': {
        if (recurrenceDays.length === 0) return t('business.components.recurrence_selector.select_days_helper', 'Select days of the week.');
        const selectedDayNames = recurrenceDays.map(d => dayNames[d]);
        const daysText = selectedDayNames.length > 1
          ? selectedDayNames.slice(0, -1).join(', ') + ' ' + t('shared.and', 'and') + ' ' + selectedDayNames[selectedDayNames.length - 1]
          : selectedDayNames[0];
        
        if (interval > 1) {
          return t('business.components.recurrence_selector.weekly_interval_helper', 'Repeats every {{count}} weeks on {{days}}', { count: interval, days: daysText });
        }
        return t('business.components.recurrence_selector.weekly_helper', 'Repeats every week on {{days}}', { days: daysText });
      }
      case 'MONTHLY': {
        if (interval > 1) {
          return t('business.components.recurrence_selector.monthly_interval_helper', 'Repeats every {{count}} months on the {{ordinal}}', { count: interval, ordinal: getOrdinal(dayOfMonth, lang) });
        }
        return t('business.components.recurrence_selector.monthly_helper', 'Repeats every month on the {{ordinal}}', { ordinal: getOrdinal(dayOfMonth, lang) });
      }
      case 'YEARLY': {
        const dateStr = scheduledDate ? new Date(scheduledDate).toLocaleDateString(t('shared.en_gb', 'en-GB'), { day: '2-digit', month: '2-digit' }) : t('shared.selected_date', 'selected date');
        if (interval > 1) {
          return t('business.components.recurrence_selector.yearly_interval_helper', 'Repeats every {{count}} years on {{date}}', { count: interval, date: dateStr });
        }
        return t('business.components.recurrence_selector.yearly_helper', 'Repeats every year on {{date}}', { date: dateStr });
      }
      default:
        return null;
    }
  };

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': { borderColor: '#86b7fe' }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#3980c0'
        : state.isFocused
          ? '#3980c0'
          : 'white',
      color: state.isSelected || state.isFocused ? 'white' : '#212529',
      '&:active': {
        backgroundColor: '#2e669a'
      }
    })
  };

  const smallSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': { borderColor: '#86b7fe' },
      minHeight: '32px',
      height: '32px'
    }),
    valueContainer: (base) => ({
      ...base,
      height: '32px',
      padding: '0 8px'
    }),
    input: (base) => ({
      ...base,
      margin: '0px',
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: '32px',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? '#3980c0'
        : state.isFocused
          ? '#3980c0'
          : 'white',
      color: state.isSelected || state.isFocused ? 'white' : '#212529',
      '&:active': {
        backgroundColor: '#2e669a'
      }
    })
  };

  return (
    <div className="recurrence-selector">
      {/* Info banner for PREVENTIVE work type - only show if showAlertInline is true */}
      {showAlertInline && workType === 'PREVENTIVE' && recurrenceType === 'NONE' && (
        <Alert color="info" className="mb-3 py-2">
          <small>
            <i className="mdi mdi-information-outline me-1"></i>
            {t('shared.preventive_work_orders_are_usually_recur', 'Preventive work orders are usually recurring. Use the recurrence field to set a regular schedule.')}
          </small>
        </Alert>
      )}

      <div className="form-group">
        <label htmlFor="recurrenceType" className="form-label fw-medium">
          {t('shared.recurrence', 'Recurrence')}
        </label>
        <Select
          id="recurrenceType"
          options={RECURRENCE_OPTIONS(t)}
          value={RECURRENCE_OPTIONS(t).find(opt => opt.value === recurrenceType) || RECURRENCE_OPTIONS(t)[0]}
          onChange={handleRecurrenceTypeChange}
          isSearchable={false}
          placeholder={t('shared.select_recurrence', 'Select Recurrence')}
          classNamePrefix="react-select"
          styles={selectStyles}
        />
        {errors.recurrenceType && <small className="text-danger">{errors.recurrenceType}</small>}
      </div>

      {/* Weekly: Interval dropdown + multi-select day toggles */}
      {recurrenceType === 'WEEKLY' && (
        <div className="mt-3">
          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="text-muted small">{t('shared.every', 'Every')}</span>
            <div style={{ width: '70px' }}>
              <Select
                options={INTERVAL_OPTIONS.WEEKLY}
                value={INTERVAL_OPTIONS.WEEKLY.find(opt => opt.value === Number(recurrenceInterval)) || INTERVAL_OPTIONS.WEEKLY[0]}
                onChange={handleIntervalChange}
                isSearchable={false}
                classNamePrefix="react-select"
                styles={smallSelectStyles}
              />
            </div>
            <span className="text-muted small">{t('shared.week_on', 'week on')}</span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {DAYS_OF_WEEK(t).map(day => (
              <button
                key={day.value}
                type="button"
                className={`btn ${recurrenceDays.includes(day.value) ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => handleDayToggle(day.value)}
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '50%',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
              >
                {day.label}
              </button>
            ))}
          </div>
          {errors.recurrenceDays && <small className="text-danger d-block mt-1">{errors.recurrenceDays}</small>}
        </div>
      )}

      {/* Monthly: Interval dropdown + day of month */}
      {recurrenceType === 'MONTHLY' && (
        <div className="mt-3">
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">{t('shared.every', 'Every')}</span>
            <div style={{ width: '70px' }}>
              <Select
                options={INTERVAL_OPTIONS.MONTHLY}
                value={INTERVAL_OPTIONS.MONTHLY.find(opt => opt.value === Number(recurrenceInterval)) || INTERVAL_OPTIONS.MONTHLY[0]}
                onChange={handleIntervalChange}
                isSearchable={false}
                classNamePrefix="react-select"
                styles={smallSelectStyles}
              />
            </div>
            <span className="text-muted small">{t('shared.month_on_the', 'month on the')}</span>
            <div style={{ width: '80px' }}>
              <Select
                options={dayOfMonthOptions}
                value={dayOfMonthOptions.find(opt => opt.value === Number(recurrenceDayOfMonth)) || dayOfMonthOptions[0]}
                onChange={handleDayOfMonthChange}
                isSearchable
                classNamePrefix="react-select"
                styles={smallSelectStyles}
              />
            </div>
          </div>
        </div>
      )}

      {/* Yearly: Interval dropdown */}
      {recurrenceType === 'YEARLY' && (
        <div className="mt-3">
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">{t('shared.every', 'Every')}</span>
            <div style={{ width: '70px' }}>
              <Select
                options={INTERVAL_OPTIONS.YEARLY}
                value={INTERVAL_OPTIONS.YEARLY.find(opt => opt.value === Number(recurrenceInterval)) || INTERVAL_OPTIONS.YEARLY[0]}
                onChange={handleIntervalChange}
                isSearchable={false}
                classNamePrefix="react-select"
                styles={smallSelectStyles}
              />
            </div>
            <span className="text-muted small">{t('shared.year', 'year')}</span>
          </div>
        </div>
      )}

      {/* Helper text */}
      {recurrenceType !== 'NONE' && (
        <div className="text-muted small mt-2">
          {getHelperText()}
        </div>
      )}
    </div>
  );
};

export default RecurrenceSelector;
